import { PurchaseRequest } from "src/api/generated/erp/db/types/tables/purchaseRequest.ts";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { SupplierPartView } from "src/api/generated/erp/db/types/tables/supplierPartView.ts";
import i18n from "i18next";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import {
	PurchaseRequestFormApi,
	PurchaseRequestFormApi_InitialData,
} from "src/api/generated/erp/purchase/purchaseRequest/purchaseRequestFormApi.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { requiredNonNegativeFloatRule, requireRule } from "src/components/common/forms/validation.ts";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { SupplierQueryApi } from "src/api/generated/erp/purchase/suppliers/api/supplierQueryApi.ts";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { currentLocalDateAsIsoString } from "src/utils/dayjsUtils.ts";
import { useState } from "react";
import { DefaultValues } from "react-hook-form";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PurchaseRequestFormProps extends FormCommonProps<number> {
	purchaseRequestId: number | undefined;
}

interface FormValues extends PurchaseRequest {
	part: PartView | null;
}

export const PurchaseRequestForm = ({ purchaseRequestId, onFormEdited, onCompleted }: PurchaseRequestFormProps) => {
	return (
		<AsyncForm<PurchaseRequestFormApi_InitialData, FormValues, number>
			fetch={() =>
				PurchaseRequestFormApi.getInitialData({
					purchaseRequestId: purchaseRequestId ?? undefined,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submitForm}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.purchase.purchaseRequest.form"),
				data: { purchaseRequestId: purchaseRequestId ?? null },
			}}
			render={(params) => <FormContent {...params} />}
		/>
	);

	function getDefaultValues({ purchaseRequest }: PurchaseRequestFormApi_InitialData): DefaultValues<FormValues> {
		if (purchaseRequest != null) {
			return {
				...purchaseRequest,
				part: undefined, // Not needed when editing existing
			};
		}
		return {
			demandDate: currentLocalDateAsIsoString(),
			quantity: 1,
			part: undefined,
		};
	}

	async function submitForm(values: FormValues): Promise<number> {
		if (purchaseRequestId != null) {
			await PurchaseRequestFormApi.update({
				purchaseRequest: values,
			});
			return purchaseRequestId;
		} else {
			return await PurchaseRequestFormApi.insert({
				supplierPartId: values.supplierPartId,
				quantity: values.quantity,
				demandDate: values.demandDate,
			});
		}
	}
};

interface FormContentProps extends AsyncFormContentParams<PurchaseRequestFormApi_InitialData, FormValues> {}

const FormContent = ({ control, data, setValue, watch }: FormContentProps) => {
	const { defaultSiteId } = useGlobalInitData();
	const { withErrorHandling } = useErrorDialog();
	const isCreate = data.purchaseRequest == null;
	const [supplierPartOptions, setSupplierPartOptions] = useState<SupplierPartView[]>(
		data.supplierPartIdOptions ?? [],
	);
	const part = watch("part");

	return (
		<>
			{isCreate && (
				<FormAsyncSelectField
					control={control}
					name={"part"}
					formValueType={"option"}
					label={i18n.t("part")}
					getOptionKey={(o: PartView) => o.partId}
					getOptionLabel={(o) => concatWithPipe(o.partNo, o.partDescription_1, o.partDescription_2)}
					fetchOptions={({ currentSelection, searchQuery }) =>
						SitePartQueryApi.getPartSelectionOptions({
							currentPartId: currentSelection,
							searchQuery,
							siteId: defaultSiteId,
						})
					}
					rules={requireRule()}
					onChange={(newPart) =>
						withErrorHandling(async () => {
							await onPartChanged(newPart);
						})
					}
				/>
			)}
			<FormDateField control={control} name={"demandDate"} label={i18n.t("demand_date")} rules={requireRule()} />
			<FormSelectField
				control={control}
				name={"supplierPartId"}
				label={i18n.t("supplier")}
				disableClearable
				options={supplierPartOptions}
				getOptionKey={(o) => o.supplierPartId}
				getOptionLabel={(o) => o.supplierName}
				rules={requireRule()}
				disabled={isCreate && part == null}
			/>
			<FormTextField
				control={control}
				name={"quantity"}
				label={i18n.t("quantity")}
				type={"number"}
				rules={requiredNonNegativeFloatRule()}
			/>
		</>
	);

	async function onPartChanged(newPart: PartView | null) {
		if (newPart == null) {
			setSupplierPartOptions([]);
			setValue("supplierPartId", null);
			return;
		}
		const options = await SupplierQueryApi.getPartSupplierOptions({
			partId: newPart.partId,
		});
		setSupplierPartOptions(options);
		const defaultOption = options.find((o) => o.isDefault) ?? options[0];
		setValue("supplierPartId", defaultOption?.supplierPartId ?? null);
	}
};
